import { z } from "zod"

/**
 * Schema for creating/editing feed records.
 * Validation rules match backend constraints.
 */
export const feedRecordSchema = z.object({
  // Required fields
  feedType: z
    .string({ message: "Feed type is required" })
    .min(1, "Feed type is required")
    .max(255, "Feed type must be less than 255 characters"),

  feedBrand: z
    .string({ message: "Feed brand is required" })
    .min(1, "Feed brand is required")
    .max(255, "Feed brand must be less than 255 characters"),

  totalFeed: z
    .number({ message: "Total feed is required" })
    .min(0, "Total feed must be at least 0"),

  maximumFeed: z
    .number({ message: "Maximum feed is required" })
    .min(0, "Maximum feed must be at least 0"),

  appetite: z
    .number({ message: "Appetite rating is required" })
    .min(1, "Appetite must be between 1-5")
    .max(5, "Appetite must be between 1-5"),

  feedTime: z.date({ message: "Feed date/time is required" }),

  // Optional fields
  notes: z.string().max(2000, "Notes must be less than 2000 characters").optional().nullable(),

  imageIds: z.array(z.string()).max(5, "Maximum 5 images allowed").optional(),
})

export type FeedRecordFormData = z.infer<typeof feedRecordSchema>

/**
 * Convert form data to API request format.
 */
export function toApiRequest(data: FeedRecordFormData) {
  // Calculate feed efficiency
  const feedEfficiency =
    data.maximumFeed > 0 ? (data.totalFeed / data.maximumFeed) * 100 : 0

  return {
    feedType: data.feedType,
    feedBrand: data.feedBrand,
    totalFeed: data.totalFeed,
    maximumFeed: data.maximumFeed,
    appetite: data.appetite,
    feedEfficiency: Math.round(feedEfficiency * 100) / 100,
    feedTime: data.feedTime.toISOString(),
    ...(data.notes && { notes: data.notes }),
    ...(data.imageIds && data.imageIds.length > 0 && { imageIds: data.imageIds }),
  }
}
